import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { BLOCK_TYPES } from './blockTypes';

// Default properties for different block types
const getDefaultProperties = (type) => {
  const commonProps = {
    padding: { top: 10, right: 10, bottom: 10, left: 10 },
    margin: { top: 0, right: 0, bottom: 0, left: 0 },
    backgroundColor: 'transparent',
    border: { width: 0, style: 'solid', color: '#000000' },
    borderRadius: 0
  };

  switch (type) {
    case BLOCK_TYPES.HEADER:
      return {
        ...commonProps,
        level:'h1',
        title: 'Header Title',
        fontSize: 32,
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'bold',
        color: '#000000',
        textAlign: 'center',
        lineHeight: 1.2,
      };

    case BLOCK_TYPES.TEXT:
      return {
        ...commonProps,
        content: 'Enter your text here',
        htmlTag: 'p',
        useStrong: false,
        fontSize: 16,
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'normal',
        color: '#000000',
        textAlign: 'left',
        lineHeight: 1.5,
        textDecoration: 'none'
      };
    
    case BLOCK_TYPES.IMAGE:
      return {
        ...commonProps,
        src: 'https://picsum.photos/200',
        alt: 'Image',
        width: '100%',
        height: 'auto',
        objectFit: 'cover',
        link: '',
        linkTarget: '_blank',
        textAlign: 'center',
        imgborderRadius:0,
      };
    
    case BLOCK_TYPES.BUTTON:
      return {
        ...commonProps,
        buttonpadding: { top: 10, right: 10, bottom: 10, left: 10 },
        buttonmargin: { top: 0, right: 0, bottom: 0, left: 0 },
        buttonborderRadius:4,
        buttonbackgroundColor:'#007bff',
        display: 'inline-block',
        text: 'Click Me',
        link: '#',
        linkTarget: '_blank',
        fontSize: 16,
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'bold',
        color: '#ffffff',
        textAlign: 'center',
        width: 'auto',
        borderRadius: 4,
        hoverBackgroundColor: '#0056b3',
        hoverColor: '#ffffff'
      };
    
    case BLOCK_TYPES.CONTAINER:
      return {
        ...commonProps,
        width: '100%',
        minHeight: 100,
        direction: 'column',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        gap: 10,
        maxWidth: '100%'
      };

    case BLOCK_TYPES.COLUMN:
      return {
        ...commonProps,
        width: '100%', // Default to 50% width
        minHeight: 50,
        direction: 'column',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        gap: 10,
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '0%'
      };
    case BLOCK_TYPES.COLUMNCONTAINER:
      return {
        ...commonProps,
        width:'100%',
        minHeight:100,
        columnnumbers:3,
        column1width:'auto',
        column2width:'auto',
        column3width:'auto',
        column4width:'auto',
        column5width:'auto',
        columngap:'5px',
        alignItems:'center',

      }
    case BLOCK_TYPES.DIVIDER:
      return {
       padding: { top: 10, right: 0, bottom: 10, left: 0 },
        margin: { top: 0, right: 0, bottom: 0, left: 0 },
        backgroundColor: 'transparent',
        border: { width: 0, style: 'solid', color: '#000000' },
    borderRadius: 0,
        height: 1,
        dividerbackgroundColor: '#cccccc',
        width: '100%',
        style: 'solid' // solid, dashed, dotted
      };
    
    case BLOCK_TYPES.SPACER:
      return {
        ...commonProps,
        height: 20,
        width: '100%'
      };

    case BLOCK_TYPES.HTMLCONTENT:
      return {
        ...commonProps,
        htmlContent: '<p>Enter your HTML content here</p>',
        sanitize: true, // Whether to sanitize HTML
        allowedTags: ['p', 'div', 'span', 'br', 'strong', 'em', 'u', 'a', 'img', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
        allowedAttributes: ['href', 'src', 'alt', 'title', 'style', 'class'],
        fontSize: 16,
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'normal',
        lineHeight: 1.5,
        color: '#000000',
        textAlign: 'left',
      };

    case BLOCK_TYPES.LISTITEMS:
      return {
        ...commonProps,
        listType: 'ul', // 'ul' or 'ol'
        items: ['List item 1', 'List item 2', 'List item 3'],
        listStyleType: 'disc', // disc, circle, square for ul; decimal, lower-alpha, upper-alpha, lower-roman, upper-roman for ol
        fontSize: 16,
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'normal',
        color: '#000000',
        textAlign: 'left',
        lineHeight: 1.5,
        itemSpacing: 8, // spacing between list items in px
        markerColor: '#000000', // color of bullets/numbers
      };
    
    default:
      return commonProps;
  }
};

// Default global properties
const defaultGlobalProperties = {
  
  backgroundColor: '#ffffff',
  emailWidth: 600,
  emailBackgroundColor:'#e0e0e0',
  emailBorderRadius: 4,
  emailBorderWidth: 0,
  emailBorderColor: '#000000',
  fontFamily: 'Arial, sans-serif',
  fontSize: 14,
  lineHeight: 1.4,
  color: '#000000',
  padding: { top: 0, right: 0, bottom: 0, left: 0 },
  margin: { top: 10, right: 'auto', bottom: 10, left: 'auto' },
  preheaderText: '',
  footerText: '',
  templateName: 'New Template',
  // Mobile responsiveness settings
  viewMode : 'default', 
  mobileBreakpoint: 480,
  mobileEmailWidth: '100%',
  mobilePadding: { top: 10, right: 10, bottom: 10, left: 10 }
};

const useEmailEditorStore = create(
  devtools(
    (set, get) => ({
      // State
      blocks: [],
      selectedBlockId: null,
      globalProperties: defaultGlobalProperties,
      history: [],
      historyIndex: -1,
      isDirty: false,
      draggedBlockId: null,
      dragOverBlockId: null,

      // Block management actions
      addBlock: (type, parentId = null, index = null) => {
        const newBlock = {
          id: `block_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
          type,
          properties: getDefaultProperties(type),
          parentId,
          children: [BLOCK_TYPES.CONTAINER,BLOCK_TYPES.COLUMNCONTAINER, BLOCK_TYPES.COLUMN].includes(type) ? [] : undefined,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        set((state) => {
          let newBlocks;
          
          if (parentId) {
            // Add to container or column
            newBlocks = state.blocks.map(block => {
              if (block.id === parentId && block.children) {
                const updatedChildren = [...block.children];
                if (index !== null && index >= 0 && index <= updatedChildren.length) {
                  updatedChildren.splice(index, 0, newBlock);
                } else {
                  updatedChildren.push(newBlock);
                }
                return { ...block, children: updatedChildren, updatedAt: new Date().toISOString() };
              }
              // Handle nested containers/columns
              if (block.children) {
                return { ...block, children: get().addBlockToChildren(block.children, parentId, newBlock, index) };
              }
              return block;
            });
          } else {
            // Add to root level
            newBlocks = [...state.blocks];
            if (index !== null && index >= 0 && index <= newBlocks.length) {
              newBlocks.splice(index, 0, newBlock);
            } else {
              newBlocks.push(newBlock);
            }
          }

          return {
            blocks: newBlocks,
            selectedBlockId: newBlock.id,
            isDirty: true
          };
        });

        get().saveToHistory();
        return newBlock.id;
      },

      // Helper function for adding blocks to nested children
      addBlockToChildren: (children, parentId, newBlock, index) => {
        return children.map(child => {
          if (child.id === parentId && child.children) {
            const updatedChildren = [...child.children];
            if (index !== null && index >= 0 && index <= updatedChildren.length) {
              updatedChildren.splice(index, 0, newBlock);
            } else {
              updatedChildren.push(newBlock);
            }
            return { ...child, children: updatedChildren, updatedAt: new Date().toISOString() };
          }
          if (child.children) {
            return { ...child, children: get().addBlockToChildren(child.children, parentId, newBlock, index) };
          }
          return child;
        });
      },

      removeBlock: (blockId) => {
        set((state) => {
          const removeFromBlocks = (blocks) => {
            return blocks.filter(block => {
              if (block.id === blockId) {
                return false;
              }
              if (block.children) {
                block.children = removeFromBlocks(block.children);
              }
              return true;
            });
          };

          return {
            blocks: removeFromBlocks(state.blocks),
            selectedBlockId: state.selectedBlockId === blockId ? null : state.selectedBlockId,
            isDirty: true
          };
        });

        get().saveToHistory();
      },

      duplicateBlock: (blockId) => {
        const findBlock = (blocks, id) => {
          for (const block of blocks) {
            if (block.id === id) return block;
            if (block.children) {
              const found = findBlock(block.children, id);
              if (found) return found;
            }
          }
          return null;
        };

        const blockToDuplicate = findBlock(get().blocks, blockId);
        if (!blockToDuplicate) return;

        const duplicateBlockRecursive = (block) => {
          const duplicated = {
            ...block,
            id: `block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };

          if (block.children) {
            duplicated.children = block.children.map(duplicateBlockRecursive);
          }

          return duplicated;
        };

        const duplicatedBlock = duplicateBlockRecursive(blockToDuplicate);

        set((state) => {
          const addAfterBlock = (blocks, targetId, newBlock) => {
            const result = [];
            for (let i = 0; i < blocks.length; i++) {
              result.push(blocks[i]);
              if (blocks[i].id === targetId) {
                result.push(newBlock);
              } else if (blocks[i].children) {
                blocks[i].children = addAfterBlock(blocks[i].children, targetId, newBlock);
              }
            }
            return result;
          };

          return {
            blocks: addAfterBlock(state.blocks, blockId, duplicatedBlock),
            selectedBlockId: duplicatedBlock.id,
            isDirty: true
          };
        });

        get().saveToHistory();
        return duplicatedBlock.id;
      },

      moveBlock: (blockId, newParentId, newIndex) => {
        set((state) => {
          let blockToMove = null;
          
          // Remove block from current position
          const removeBlock = (blocks) => {
            return blocks.filter(block => {
              if (block.id === blockId) {
                blockToMove = { ...block };
                return false;
              }
              if (block.children) {
                block.children = removeBlock(block.children);
              }
              return true;
            });
          };

          let newBlocks = removeBlock([...state.blocks]);

          if (!blockToMove) return state;

          // Update parent reference
          blockToMove.parentId = newParentId;
          blockToMove.updatedAt = new Date().toISOString();

          // Add block to new position
          const addBlockToPosition = (blocks, parentId, block, index) => {
            if (!parentId) {
              // Add to root level
              blocks.splice(index !== null ? index : blocks.length, 0, block);
              return blocks;
            }

            return blocks.map(b => {
              if (b.id === parentId && b.children) {
                const updatedChildren = [...b.children];
                updatedChildren.splice(index !== null ? index : updatedChildren.length, 0, block);
                return { ...b, children: updatedChildren, updatedAt: new Date().toISOString() };
              }
              if (b.children) {
                return { ...b, children: addBlockToPosition(b.children, parentId, block, index) };
              }
              return b;
            });
          };

          newBlocks = addBlockToPosition(newBlocks, newParentId, blockToMove, newIndex);

          return {
            blocks: newBlocks,
            isDirty: true
          };
        });

        get().saveToHistory();
      },

      // Column-specific actions
      addColumn: (containerId, columnWidth = '50%') => {
        const columnId = get().addBlock(BLOCK_TYPES.COLUMN, containerId);
        get().updateBlockProperties(columnId, { width: columnWidth });
        return columnId;
      },

      updateColumnWidths: (columnIds, widths) => {
        columnIds.forEach((columnId, index) => {
          if (widths[index]) {
            get().updateBlockProperties(columnId, { width: widths[index] });
          }
        });
      },

      // Drag and drop actions
      setDraggedBlock: (blockId) => {
        set({ draggedBlockId: blockId });
      },

      setDragOverBlock: (blockId) => {
        set({ dragOverBlockId: blockId });
      },

      clearDragState: () => {
        set({ draggedBlockId: null, dragOverBlockId: null });
      },

      // Selection actions
      selectBlock: (blockId) => {
        set({ selectedBlockId: blockId });
      },

      clearSelection: () => {
        set({ selectedBlockId: null });
      },

      // Properties actions
      updateBlockProperties: (blockId, properties) => {
        set((state) => {
          const updateInBlocks = (blocks) => {
            return blocks.map(block => {
              if (block.id === blockId) {
                return {
                  ...block,
                  properties: { ...block.properties, ...properties },
                  updatedAt: new Date().toISOString()
                };
              }
              if (block.children) {
                return { ...block, children: updateInBlocks(block.children) };
              }
              return block;
            });
          };

          return {
            blocks: updateInBlocks(state.blocks),
            isDirty: true
          };
        });

        get().saveToHistory();
      },

      updateGlobalProperties: (properties) => {
        set((state) => ({
          globalProperties: { ...state.globalProperties, ...properties },
          isDirty: true
        }));

        get().saveToHistory();
      },

      // Utility actions
      getSelectedBlock: () => {
        const { blocks, selectedBlockId } = get();
        if (!selectedBlockId) return null;

        const findBlock = (blocks, id) => {
          for (const block of blocks) {
            if (block.id === id) return block;
            if (block.children) {
              const found = findBlock(block.children, id);
              if (found) return found;
            }
          }
          return null;
        };

        return findBlock(blocks, selectedBlockId);
      },

      getAllBlocks: () => {
        const { blocks } = get();
        const flattenBlocks = (blocks) => {
          let result = [];
          blocks.forEach(block => {
            result.push(block);
            if (block.children) {
              result = result.concat(flattenBlocks(block.children));
            }
          });
          return result;
        };

        return flattenBlocks(blocks);
      },

      getBlockById: (blockId) => {
        const { blocks } = get();
        const findBlock = (blocks, id) => {
          for (const block of blocks) {
            if (block.id === id) return block;
            if (block.children) {
              const found = findBlock(block.children, id);
              if (found) return found;
            }
          }
          return null;
        };

        return findBlock(blocks, blockId);
      },

      getParentBlock: (blockId) => {
        const { blocks } = get();
        const findParent = (blocks, targetId, parent = null) => {
          for (const block of blocks) {
            if (block.id === targetId) return parent;
            if (block.children) {
              const found = findParent(block.children, targetId, block);
              if (found) return found;
            }
          }
          return null;
        };

        return findParent(blocks, blockId);
      },

      getChildrenBlocks: (blockId) => {
        const block = get().getBlockById(blockId);
        return block?.children || [];
      },

      // History management
      saveToHistory: () => {
        set((state) => {
          const currentState = {
            blocks: state.blocks,
            globalProperties: state.globalProperties,
            timestamp: Date.now()
          };

          const newHistory = state.history.slice(0, state.historyIndex + 1);
          newHistory.push(currentState);

          // Limit history to 50 states
          if (newHistory.length > 50) {
            newHistory.shift();
          }

          return {
            history: newHistory,
            historyIndex: newHistory.length - 1
          };
        });
      },

      undo: () => {
        const { history, historyIndex } = get();
        if (historyIndex > 0) {
          const previousState = history[historyIndex - 1];
          set({
            blocks: previousState.blocks,
            globalProperties: previousState.globalProperties,
            historyIndex: historyIndex - 1,
            selectedBlockId: null,
            isDirty: true
          });
        }
      },

      redo: () => {
        const { history, historyIndex } = get();
        if (historyIndex < history.length - 1) {
          const nextState = history[historyIndex + 1];
          set({
            blocks: nextState.blocks,
            globalProperties: nextState.globalProperties,
            historyIndex: historyIndex + 1,
            selectedBlockId: null,
            isDirty: true
          });
        }
      },

      canUndo: () => {
        const { historyIndex } = get();
        return historyIndex > 0;
      },

      canRedo: () => {
        const { history, historyIndex } = get();
        return historyIndex < history.length - 1;
      },

      // Reset and load actions
      resetEditor: () => {
        set({
          blocks: [],
          selectedBlockId: null,
          globalProperties: defaultGlobalProperties,
          history: [],
          historyIndex: -1,
          isDirty: false,
          draggedBlockId: null,
          dragOverBlockId: null
        });
      },

      loadTemplate: (template) => {
        // Handle both old format (blocks/globalProperties) and new export format (structure.blocks/settings)
        const blocks = template.blocks || template.structure?.blocks || [];
        const globalProps = template.globalProperties || template.settings || {};
        
       // console.log('loadTemplate - blocks:', blocks);
       // console.log('loadTemplate - globalProps:', globalProps);
        
        set({
          blocks: blocks,
          globalProperties: { ...defaultGlobalProperties, ...globalProps },
          selectedBlockId: null,
          isDirty: false,
          draggedBlockId: null,
          dragOverBlockId: null
        });
        
        get().saveToHistory();
      },

      exportTemplate: () => {
        const { blocks, globalProperties } = get();
        return {
          version: '2.0',
          metadata: {
            subject: null,
            createdAt: new Date().toISOString(),
            lastModified: new Date().toISOString(),
            template: true
          },
          structure: {
            blocks: blocks
          },
          settings: globalProperties,
          exportedAt: new Date().toISOString()
        };
      },

      // Validation
      validateEmail: () => {
        const { blocks, globalProperties } = get();
        const errors = [];
        const warnings = [];

        // Validate global properties
      /*  if (!globalProperties.subject?.trim()) {
          warnings.push('Email subject is empty');
        }*/

        if (globalProperties.emailWidth < 320) {
          errors.push('Email width should be at least 320px for mobile compatibility');
        }

      /*  if (!globalProperties.fromEmail?.trim()) {
          warnings.push('From email address is not set');
        }
    */
        // Validate blocks
        const validateBlock = (block, path = '') => {
          const blockPath = path ? `${path} > ${block.type}` : block.type;

          switch (block.type) {
            case BLOCK_TYPES.HEADER:
              if (!block.properties.title?.trim()) {
                errors.push(`Header block at ${blockPath} is missing title`);
              }
              break;

            case BLOCK_TYPES.IMAGE:
              if (!block.properties.src?.trim()) {
                errors.push(`Image block at ${blockPath} is missing source URL`);
              }
              if (!block.properties.alt?.trim()) {
                warnings.push(`Image block at ${blockPath} is missing alt text`);
              }
              break;

            case BLOCK_TYPES.BUTTON:
              if (!block.properties.text?.trim()) {
                errors.push(`Button block at ${blockPath} is missing text`);
              }
              if (!block.properties.link?.trim() || block.properties.link === '#') {
                warnings.push(`Button block at ${blockPath} has no link or placeholder link`);
              }
              break;

            case BLOCK_TYPES.TEXT:
              if (!block.properties.content?.trim()) {
                warnings.push(`Text block at ${blockPath} is empty`);
              }
              break;

            case BLOCK_TYPES.HTMLCONTENT:
              if (!block.properties.htmlContent?.trim()) {
                warnings.push(`HTML Content block at ${blockPath} is empty`);
              }
              // Basic HTML validation
              const htmlContent = block.properties.htmlContent;
              if (htmlContent && htmlContent.includes('<script')) {
                errors.push(`HTML Content block at ${blockPath} contains script tags which are not allowed in emails`);
              }
              break;

            case BLOCK_TYPES.CONTAINER:
              if (!block.children || block.children.length === 0) {
                warnings.push(`Container block at ${blockPath} is empty`);
              }
              break;

            case BLOCK_TYPES.COLUMN:
              if (!block.children || block.children.length === 0) {
                warnings.push(`Column block at ${blockPath} is empty`);
              }
              break;

            default:
              // No validation for other block types
              break;
          }

          if (block.children) {
            block.children.forEach(child => validateBlock(child, blockPath));
          }
        };

        blocks.forEach(block => validateBlock(block));

        // Check for column layout issues
        const validateColumnLayouts = (blocks) => {
          blocks.forEach(block => {
            if (block.type === BLOCK_TYPES.CONTAINER && block.children) {
              const columns = block.children.filter(child => child.type === BLOCK_TYPES.COLUMN);
              if (columns.length > 0) {
                const totalWidth = columns.reduce((sum, col) => {
                  const width = parseFloat(col.properties.width.replace('%', ''));
                  return sum + (isNaN(width) ? 0 : width);
                }, 0);
                
                if (Math.abs(totalWidth - 100) > 1) {
                  warnings.push(`Column widths in container don't add up to 100% (current: ${totalWidth}%)`);
                }
              }
              validateColumnLayouts(block.children);
            } else if (block.children) {
              validateColumnLayouts(block.children);
            }
          });
        };

        validateColumnLayouts(blocks);

        return { errors, warnings, isValid: errors.length === 0 };
      },

      // Search and filter
      searchBlocks: (query) => {
        const blocks = get().blocks;
        const results = [];
        const searchTerm = query.toLowerCase();

        const searchInBlock = (block) => {
          const blockMatches = [];
          
          // Search in block properties
          const properties = block.properties || {};
          Object.entries(properties).forEach(([key, value]) => {
            if (typeof value === 'string' && value.toLowerCase().includes(searchTerm)) {
              blockMatches.push({ key, value });
            }
          });
          
          if (blockMatches.length > 0) {
            results.push({
              blockId: block.id,
              blockType: block.type,
              matches: blockMatches
            });
          }
          
          if (block.children) {
            block.children.forEach(child => searchInBlock(child));
          }
        };
        
        blocks.forEach(block => searchInBlock(block));
        return results;
      },

      // Performance and optimization
      getBlocksCount: () => {
        return get().getAllBlocks().length;
      },

      getBlocksByType: (type) => {
        return get().getAllBlocks().filter(block => block.type === type);
      },

      optimizeTemplate: () => {
        const actions = [];
        const allBlocks = get().getAllBlocks();
        
        // Find empty containers
        const emptyContainers = allBlocks.filter(block => 
          [BLOCK_TYPES.CONTAINER, BLOCK_TYPES.COLUMN].includes(block.type) && 
          (!block.children || block.children.length === 0)
        );
        
        if (emptyContainers.length > 0) {
          actions.push({
            type: 'remove_empty_containers',
            count: emptyContainers.length,
            blocks: emptyContainers
          });
        }
        
        // Find unused spacers (consecutive spacers)
        const spacers = allBlocks.filter(block => block.type === BLOCK_TYPES.SPACER);
        const redundantSpacers = [];
        
        spacers.forEach(spacer => {
          const parent = get().getParentBlock(spacer.id);
          const siblings = parent ? parent.children : get().blocks;
          const spacerIndex = siblings.findIndex(s => s.id === spacer.id);
          
          if (spacerIndex > 0 && siblings[spacerIndex - 1].type === BLOCK_TYPES.SPACER) {
            redundantSpacers.push(spacer);
          }
        });
        
        if (redundantSpacers.length > 0) {
          actions.push({
            type: 'remove_redundant_spacers',
            count: redundantSpacers.length,
            blocks: redundantSpacers
          });
        }
        
        return actions;
      }
    }),
    {
      name: 'email-editor-store',
      partialize: (state) => ({
        blocks: state.blocks,
        globalProperties: state.globalProperties
      })
    }
  )
);

export default useEmailEditorStore;